import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  fetchDataWithLoading, 
  crudOperations, 
  createDebouncedSearch 
} from "../utils/commonFetchData";
import { setModuleFilter, resetModuleFilter, selectModuleFilter, setModuleSorting } from "../store/slices/filterSorting.slice";

/**
 * Custom hook for common data fetching operations
 * Reduces code duplication across components
 * @param {Function} apiService - API service function
 * @param {Object} options - Configuration options
 * @returns {Object} State and methods for data management
 */
export const useCommonFetch = (apiService, options = {}) => {
  const { limit } = useSelector((state) => state.global);
  const dispatch = useDispatch(); 
  // Default options
  const {
    dataPath = "data",
    totalPath = "meta.total",
    errorMessage = "Failed to fetch data!",
    successMessage = "Data fetched successfully!",
    showSuccessToast = false,
    showErrorToast = true,
    debounceDelay = 500,
    autoFetch = true,
    dataTransformer = null, // Add data transformer option,
    customParams=null,
    additionalParams=null, // Additional params that can change dynamically
    id=null,
    // Sorting options
    sortingOptions = null, // e.g., ["Date", "Name"] or [{label: "Date", field: "created_at"}]
    sortingModule = "default", // redux slice key for sorting state
    orderParamNames = { orderByKey: "order_by", orderTypeKey: "order_type", includeDisabledKey: "includeDisabled" },
    sortingDisableText = "",
    sortingShowDisableToggle = true,
    // Custom sorting button text - can be global or per-option
    sortingButtonText = {
      ascending: "A-Z",
      descending: "Z-A"
    },
    // Filter options
    initialFilters = {}, // any default filters
    filterModule = "default", // Redux module name for filter persistence
  } = options;
 
  // State management
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [action, setAction] = useState("");
  const [error, setError] = useState(null);
  const [extraParams, setExtraParams] = useState(customParams || {});
  // Get persisted filter state from Redux
  const persistedFilters = useSelector((state) => selectModuleFilter(state, filterModule));
  const [filterParams, setFilterParams] = useState(persistedFilters?.filters || initialFilters || {});

  // Refs for cleanup
  const searchTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const isInitialMount = useRef(true);
  const prevLimitRef = useRef(limit);

  // Normalize sorting options into [{label, field, buttonText}] for up to 3 slots (date/name/other)
  const normalizedSorting = useMemo(() => {
    if (!sortingOptions || !Array.isArray(sortingOptions)) return null;
    const toItem = (opt) => {
      if (typeof opt === "string") {
        const label = opt;
        const lc = opt.toLowerCase();
        const field = lc === "date" ? "created_at" : lc;
        return { 
          label, 
          field,
          buttonText: sortingButtonText // Use global button text
        };
      }
      if (opt && typeof opt === "object") {
        return { 
          label: opt.label, 
          field: opt.field || opt.label?.toLowerCase(),
          buttonText: opt.buttonText || sortingButtonText // Use option-specific or fallback to global
        };
      }
      return null;
    };
    const items = sortingOptions.map(toItem).filter(Boolean).slice(0, 3);

    // Prefer semantic placement so a single "Name" option doesn't become "Date" slot.
    let date = null;
    let name = null;
    let other = null;
    for (const it of items) {
      const f = String(it?.field || "").toLowerCase();
      const l = String(it?.label || "").toLowerCase();

      const isDateLike = f === "created_at" || f.includes("date") || l.includes("date");
      const isNameLike = f === "name" || l === "name" || l.includes("name");

      if (!date && isDateLike) {
        date = it;
        continue;
      }
      if (!name && isNameLike) {
        name = it;
        continue;
      }
      if (!other) other = it;
    }

    // Back-compat fallback: if nothing matched, keep original positional behavior.
    if (!date && !name && !other) {
      return {
        date: items[0] || null,
        name: items[1] || null,
        other: items[2] || null,
      };
    }

    // Ensure we still fill remaining slots deterministically if possible.
    if (!date && items[0] && items[0] !== name && items[0] !== other) date = items[0];
    if (!name && items[1] && items[1] !== date && items[1] !== other) name = items[1];
    if (!other && items[2] && items[2] !== date && items[2] !== name) other = items[2];

    return { date, name, other };
  }, [sortingOptions, sortingButtonText]);

  // State setters for the utility
  const stateSetters = {
    setLoading,
    setData,
    setTotalData: setTotal,
    setError
  };

  // Fetch data function
  const fetchData = useCallback(async (customParamsArg = {},reload=false,id=null) => {
    if (!reload && !mountedRef.current) return;
    
    // Early return if apiService is not provided or is not a function
    if (!apiService || typeof apiService !== 'function') {
      console.warn('useCommonFetch: apiService is not provided or is not a function');
      if (setError) setError(new Error('API service not available'));
      if (setLoading) setLoading(false);
      return;
    }
 
    const { _ignoreFilters, ...customParamsCleaned } = customParamsArg || {};

    const params = {
      search,
      page: search ? 1 : currentPage,
      limit,
      // When clearing filters, explicitly ignore previous filterParams
      ...(_ignoreFilters ? {} : (filterParams || {})),
      ...(extraParams || {}),
      ...(additionalParams || {}), // Include additionalParams (like date filters)
      ...(customParamsCleaned || {})
    };

    const fetchOptions = {
      dataPath,
      totalPath,
      errorMessage,
      successMessage,
      showSuccessToast,
      showErrorToast
    };
 
    const result = await fetchDataWithLoading(
      apiService,
      params,
      stateSetters,
      fetchOptions,
      id
    ); 
    if (process.env.NODE_ENV === 'development') {
    }
    // Apply data transformation if provided
    if (result.success && dataTransformer && typeof dataTransformer === 'function') {
      const transformedData = dataTransformer(result.data);
      setData(transformedData);
    }
  }, [search, currentPage, limit, apiService, dataPath, totalPath, errorMessage, successMessage, showSuccessToast, showErrorToast, dataTransformer, extraParams, filterParams, additionalParams]);

  // Build sorting params using normalizedSorting and redux sorting state
  const buildSortingParams = useCallback((sorting) => {
    if (!normalizedSorting) return {};
    const { date, name, other } = normalizedSorting;
    let field = null;
    let direction = null;

    if (date && sorting?.isSortDate) {
      field = date.field;
      direction = sorting?.sortDate || "asc";
    } else if (name && sorting?.isSortName) {
      field = name.field;
      direction = sorting?.sortName || "asc";
    } else if (other && sorting?.isOtherSort) {
      field = other.field;
      direction = sorting?.otherSort || "asc";
    }

    const params = {};
    if (field && direction) {
      params[orderParamNames.orderByKey] = field;
      params[orderParamNames.orderTypeKey] = direction;
    }
    if (typeof sorting?.showDisabled === "boolean") {
      params[orderParamNames.includeDisabledKey] = sorting.showDisabled;
    }
    return params;
  }, [normalizedSorting, orderParamNames]);

  // Expose onSortingChange that applies and refetches
  const onSortingChange = useCallback((sorting) => {
    const params = buildSortingParams(sorting);
    setExtraParams((prev) => ({ ...prev, ...params }));
    fetchData(params, true);
  }, [buildSortingParams, fetchData]);

  // Filters: apply and clear
  const applyFilters = useCallback((filters) => {
    const newFilters = filters || {};
    setFilterParams(newFilters);
    // Persist filters to Redux
    dispatch(setModuleFilter({
      module: filterModule,
      filterState: {
        filters: newFilters,
        isFilter: Object.keys(newFilters).length > 0
      }
    }));
    setCurrentPage(1);
    fetchData(newFilters, true);
  }, [fetchData, dispatch, filterModule]);

  const clearFilters = useCallback(() => {
    setFilterParams({});
    // Clear persisted filters from Redux
    dispatch(resetModuleFilter(filterModule));
    setCurrentPage(1);
    // Ensure previous filterParams are ignored during this fetch
    fetchData({ _ignoreFilters: true }, true);
  }, [fetchData, dispatch, filterModule]);

  // Compute UI props for PageTableHeader -> AdvanceSorting
  const sortingUIProps = useMemo(() => {
    if (!normalizedSorting) return null;
    return {
      sortingModule,
      onSortingChange,
      isFilterDisable: sortingShowDisableToggle,
      disableText: sortingDisableText,
      isDateSorting: !!normalizedSorting.date,
      sortByDateText: normalizedSorting.date?.label || "",
      sortByNameText: normalizedSorting.name?.label || "",
      isOtherSortText: normalizedSorting.other?.label || "",
      // Pass button text for each sorting option
      dateSortingButtonText: normalizedSorting.date?.buttonText || sortingButtonText,
      nameSortingButtonText: normalizedSorting.name?.buttonText || sortingButtonText,
      otherSortingButtonText: normalizedSorting.other?.buttonText || sortingButtonText,
      // Global fallback
      sortingButtonText,
    };
  }, [normalizedSorting, sortingModule, onSortingChange, sortingShowDisableToggle, sortingDisableText, sortingButtonText]);

  // Debounced search function
  const debouncedSearch = useCallback(
    createDebouncedSearch((searchTerm) => {
      setSearch(searchTerm);
      setCurrentPage(1);
      fetchData({ search: searchTerm, page: 1 },true,id);
    }, debounceDelay),
    [fetchData, debounceDelay]
  );

  // Handle search change
  const handleSearchChange = useCallback((e) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);
    setCurrentPage(1);
    debouncedSearch(searchTerm);
  }, [debouncedSearch]);

  // Handle pagination
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    // Trigger fetch with the new page - use reload=true to force the fetch
    fetchData({ page }, true, id);
  }, [fetchData, id]);

  // Handle modal operations
  const openModal = useCallback((id = "", modalAction) => {
    setAction(modalAction);
    setSelectedId(id);
  }, []);

  const closeModal = useCallback(() => {
    setAction("");
    setSelectedId("");
  }, []);

  // CRUD operations
  const handleCreate = useCallback(async (createData, createApi, options = {}) => {
  try {
    const result = await crudOperations.create(createApi, createData, {
      ...options,
    });

    if (result.success) {
      fetchData({}, true, id);
      closeModal();
    }

    return result;
  } catch (err) {
    throw err; // let form handle setErrors
  }
}, [fetchData, closeModal]);
  // const handleCreate = useCallback(async (createData, createApi, options = {}) => {
  //   const result = await crudOperations.create(createApi, createData, {
  //     onSuccess: () => {
  //       fetchData({},true,id);
  //       closeModal();
  //     },
  //     ...options
  //   });
  //   return result;
  // }, [fetchData, closeModal]);

  const handleUpdate = useCallback(async (updateData,selectedId, updateApi, options = {}) => {
    const result = await crudOperations.update(updateApi, updateData, selectedId, {
      onSuccess: () => {
        fetchData({},true,id);
        closeModal();
      },
      ...options
    });
    return result;
  }, [fetchData, closeModal]);

  const softDelete = useCallback(async (updateData,selectedId, updateApi, options = {}) => {
    const result = await crudOperations.update(updateApi, updateData, selectedId, {
      onSuccess: () => {
        fetchData({},true,id);
        closeModal();
      },
      ...options
    });
    return result;
  }, [fetchData, closeModal, selectedId]);

  const handleDelete = useCallback(async (selectedId,deleteApi, options = {}) => {
    const result = await crudOperations.delete(deleteApi, selectedId, {
      onSuccess: () => {
        fetchData({},true,id);
        closeModal();
      },
      ...options
    });
    return result;
  }, [fetchData, closeModal, selectedId]);

  // Refresh data
  const refreshData = useCallback((data,refresh) => {
    if (process.env.NODE_ENV === 'development') {
    }
    fetchData(data,refresh,id);
  }, [fetchData]);

  // Reset state
  const resetState = useCallback(() => {
    setLoading(false);
    setData([]);
    setTotal(0);
    setCurrentPage(1);
    setSearch("");
    setSelectedId("");
    setAction("");
    setError(null);
  }, []);

  // Initialize sorting state for the module when hook first mounts
  useEffect(() => {
    if (sortingModule && normalizedSorting) {
      // Dispatch an empty action to trigger default state initialization
      dispatch(setModuleSorting({ 
        module: sortingModule, 
        sortingState: {} // This will trigger the default state initialization
      }));
    }
  }, [sortingModule, normalizedSorting, dispatch]);

  // Auto fetch on initial mount
  useEffect(() => {
    if (autoFetch && apiService && typeof apiService === 'function') {
      fetchData({},false,id);
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService, autoFetch]);

  // Refetch when additionalParams change (for dynamic filters like dates)
  useEffect(() => {
    if (autoFetch && apiService && typeof apiService === 'function' && additionalParams && Object.keys(additionalParams).length > 0) {
      // Only refetch if we have required params (like date_start and date_end)
      const hasRequiredParams = additionalParams.date_start && additionalParams.date_end;
      if (hasRequiredParams) {
        fetchData({}, true);
      }
    }
  }, [additionalParams, autoFetch, apiService, fetchData]);

  // Handle limit changes separately - reset to page 1 and fetch
  useEffect(() => {
    // Only trigger if limit actually changed and it's not initial mount
    if (!isInitialMount.current && prevLimitRef.current !== limit) {
      prevLimitRef.current = limit;
      if (autoFetch && apiService && typeof apiService === 'function') {
        setCurrentPage(1);
        fetchData({ page: 1 }, true, id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    loading,
    data,
    total,
    currentPage,
    search,
    selectedId,
    action,
    error,
    
    // Actions
    softDelete,
    fetchData,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    refreshData,
    resetState,
    setCustomParams: setExtraParams,
    
    // Sorting helpers and UI props
    buildSortingParams,
    onSortingChange,
    sortingUIProps,

    // Filter helpers
    applyFilters,
    clearFilters,

    // Utility functions
    crudOperations
  };
};

export default useCommonFetch; 